import {
  offerAiPrompts,
  offerAiPromptVersions,
  offerBlockTemplateRevisions,
  offerBlockTemplates,
  offerEvents,
  offerLineItems,
  offerPriceBoxes,
  offers,
  offerSettings,
} from '../offer'
import { describeTable } from './describeTable'

describeTable(offers, {
  hiddenColumns: ['webToken'],
  description: `One formal written quote (tarjous) sent to a customer, always hanging off a CRM lead (opportunity_id, required). In use since May 2026. Content is sections of text plus one or more offer_price_boxes each holding offer_line_items; the customer receives an email with a link to a public offer page where a button accepts it.
status is a denormalised cache and 'accepted' captures ONLY web-button acceptances: many offers accepted by phone or email stay 'sent' (a rep may mark them accepted manually, but often does not). Measure deal conversion on the opportunity (opportunities.stage='won'), never on offers.status. No job flips status to 'expired' — expiry is derived from valid_until at render time, so a 'sent' offer with valid_until in the past is expired. Row counts include drafts, cancelled offers and re-issued versions of the same deal (737 rows on 627 opportunities): count DISTINCT opportunity_id for "how many deals were quoted".
Sent / opened / accepted moments are in offer_events (type), not on this row. Offer value = sum over the offer's price boxes of quantity * unit_price of offer_line_items (VAT 0, euros); when several boxes exist they are alternatives (options), so use the highlighted box, not the sum of all boxes. Boxes with is_price_list = true carry no value and are skipped. ~90 % of offers are AI-drafted (ai_draft_suggested_at set); ai_draft_accepted_as_is tells whether the seller sent the AI's text unchanged.`,
  columns: {
    number: "Human-readable id 'T-<year>-<id>' shown to the customer.",
    status:
      'draft = being written, not sent; sent = delivered to the customer and open (INCLUDES offers accepted by phone/email that nobody marked); accepted = accepted via the web button or marked accepted by a rep; rejected = customer declined; expired = past validity (rarely written — derive from valid_until instead); cancelled = withdrawn by us at any stage.',
    opportunityId:
      'The CRM lead being quoted. Contact and property data live on the opportunity, not here.',
    customerId:
      'Existing customer, when the lead already was one. Usually NULL.',
    responsibleUserId: 'Seller who owns the offer.',
    primaryCategory:
      'Service category driving the template blocks: chimney (nuohous), hvac (ilmanvaihto), heat_pump, roof (katto), yard, safety, … Same vocabulary as maintenance_tasks.category.',
    validForDays: 'Validity window the seller chose (default 14).',
    validUntil:
      'sent_at + valid_for_days, end of day; NULL until sent. Past this the offer is expired even if status still says sent.',
    webToken:
      'Token in the public offer link; not readable here, so list columns instead of SELECT *.',
    serviceDescription:
      'Offer body text describing the service. summary is the short pitch, lahtotilanne the starting-situation paragraph.',
    johdanto:
      'Opening paragraph: thanks the requester by name and restates the specs they gave. NULL when there was no request to credit.',
    aikataulutus:
      'Schedule boilerplate text (seeded from offer_settings). laskutus = billing terms text.',
    ehdot:
      'Terms text (site access, retention of title), seeded from offer_settings key ehdot. Empty on offers created before the column existed.',
    workContent:
      'JSON array of {id, catalogRef?, title, items[]} — work-content sections listed on the offer.',
    optionalBlocks:
      'JSON array of blocks {id, type: trust|references|klarna|kotitalousvahennys|custom|team, title, items[] or bodyText; team blocks list memberUserIds (users.id)}.',
    profileSnapshot:
      'JSON {sellerUserId, seller, members: {"<user id>": card}} of the people shown on the public page (card = {name, jobTitle, photoUrl, phone, email, bio}), frozen at the latest send so later profile edits do not change a sent offer. NULL on drafts and on offers last sent before October 2026, which show live newsletter_sender_profiles data. seller applies only while sellerUserId = responsible_user_id; a seller switched after the send shows live.',
    edellytykset:
      'JSON string array: preconditions the customer must meet. eiSisally: JSON string array of exclusions.',
    emailSubject:
      'Persisted cover-email draft (AI-written saateviesti); emailBody is its body. Cleared on every content save.',
    showStreetView:
      'Seller toggle: false hides the Google Street View photo at the top of the offer page. Locked once the offer is sent.',
    streetView:
      'JSON {panoId, cameraLat, cameraLng, heading}: the Street View panorama frozen on the first send. NULL on drafts, on offers sent before October 2026, when the photo was hidden at send and when no panorama was found (no photo shown).',
    heroImage:
      'JSON {attachmentId, rotation, storageKey}: the seller-picked cover photo, composed from the lead attachment attachments.id = attachmentId. When set it replaces the Street View photo. NULL = Street View or no photo, per show_street_view. Locked once the offer is sent.',
    aiDraftSuggestion:
      'JSON snapshot of the last AI-generated draft {serviceDescription, summary, lahtotilanne, edellytykset, eiSisally, workContent, optionalBlocks, priceBoxes[{title, extraText, highlight, lineItems[]}]}; NULL when the seller wrote the offer by hand.',
    aiDraftSuggestedAt:
      'When the AI draft was generated. NOT NULL = AI-drafted offer (~90 %).',
    aiDraftAcceptedAsIs:
      'true = the saved offer equals the AI draft; false = seller edited it (see ai_draft_edited_fields, JSON array of field names). NULL = not evaluated / not AI-drafted.',
    aiDraftEvaluatedAt: 'When the draft-vs-saved comparison was made.',
    aiQualityScore: 'Reserved for graded quality; NULL in practice.',
  },
})

describeTable(offerPriceBoxes, {
  description: `A priced option on an offer (one offer usually has one box; several boxes are alternatives the customer chooses between, e.g. basic vs. extended). Total of a box = sum of its offer_line_items quantity * unit_price. highlight marks the recommended option — use it when picking one value per offer.`,
  columns: {
    highlight: 'true = the recommended / default option.',
    isPriceList:
      'true = a price list of unit prices with no committed quantity. Carries no offer value: exclude these boxes when picking the offer price.',
    title:
      'Box heading shown to the customer; extraText is a note under the price.',
    sortOrder: 'Display order.',
  },
})

describeTable(offerLineItems, {
  description: `One priced line inside an offer price box. unit_price is euros VAT 0 (net), up to 5 decimals so a price entered VAT-inclusive round-trips; quantity * unit_price = line net value. Prices are snapshots — catalog_id is a soft text reference to maintenance_tasks or products and may point at a since-changed or deleted catalogue row. is_labor separates labour (eligible for the household tax deduction, kotitalousvähennys) from materials.`,
  columns: {
    priceBoxId: 'Parent offer_price_boxes row; join → offers for the offer.',
    source:
      "'catalog' = picked from the service/product catalogue; 'custom' = free-text line.",
    catalogType:
      "'maintenance_task' or 'product' — which catalogue catalog_id refers to.",
    catalogId: 'Id in that catalogue as text; no FK, may be stale.',
    quantity: 'Numeric quantity in the given unit (default 1 kpl).',
    unit: "Unit label, e.g. 'kpl', 'h', 'm'.",
    unitPrice: 'Euros excluding VAT.',
    isLabor:
      'true = labour (kotitalousvähennys-eligible), false = material / product.',
    aiGenerated:
      'true = proposed by the AI draft; aiReviewedAt set once the seller confirmed or edited it.',
  },
})

describeTable(offerEvents, {
  description: `Append-only lifecycle log of an offer; the source of truth for when things happened. One row per transition: created, edited (metadata.changedFields / changedFieldValues), regenerated (AI redraft), sent (metadata.recipientSnapshot + contentSnapshot of what went out), opened (customer opened the public page; may repeat), accepted (metadata.acceptedBy, acceptedNotes, contentSnapshot; metadata.source='web_offer' when the customer clicked the button, absent when a rep marked it), rejected (rejectedReason), expired, cancelled (cancelledBy, cancelledReason).
Sent date of an offer = MIN(created_at) WHERE type='sent'. "Was it opened" = EXISTS type='opened'. user_id is the staff member who caused the event; NULL for customer actions (opened / web accept).`,
  columns: {
    type: 'created | edited | regenerated | sent | opened | accepted | rejected | expired | cancelled (see table description).',
    userId: 'Staff actor; NULL for customer-side events.',
    metadata:
      "JSON {acceptedBy, acceptedNotes, rejectedReason, cancelledBy, cancelledReason, source ('web_offer' = customer's browser), shortUrlKey, userPrompt, aiModel, changedFields[], changedFieldValues{field: {from, to}}, userAgent, recipientSnapshot{name, address, contactPerson, phone, email}, contentSnapshot{…full offer content and priceBoxes as sent/accepted}, coverMessage (sent only: the email cover text the seller wrote, null if none; absent on batch sends and sends before 30.10.2026), coverLetterSigner{name, jobTitle, photoUrl}}.",
  },
})

describeTable(offerBlockTemplates, {
  description: `Admin-managed default content blocks (trust badges, references, Klarna, kotitalousvähennys text, custom) that get loaded into an offer's optional_blocks based on its primary_category. task_category='_default' applies to all categories. Configuration, not activity.`,
  columns: {
    type: 'trust | references | klarna | kotitalousvahennys | custom | team — which block shape content holds.',
    taskCategory:
      "maintenance_tasks.category the block applies to, or '_default' for every category.",
    content:
      'JSON OfferOptionalBlock (minus id): {type, title, items[]} for trust/references, {type, title, bodyText} for the rest.',
  },
})

describeTable(offerBlockTemplateRevisions, {
  description:
    'Audit trail of offer_block_templates edits: one row per save with the full template state after the edit and who made it (edited_by_user_id).',
})

describeTable(offerSettings, {
  description:
    "Key-value defaults for offers, e.g. key='aikataulutus' and key='laskutus' boilerplate texts seeded into every new offer. A few rows of configuration.",
})

describeTable(offerAiPrompts, {
  description:
    "Editable system prompts for the offer AI: kind='suggestion' drafts the whole offer from the opportunity, kind='cover' writes the cover email (saateviesti). One row per kind; history in offer_ai_prompt_versions.",
  columns: {
    kind: "'suggestion' = full offer draft prompt; 'cover' = cover-email prompt.",
    systemInstructions: 'The live prompt text.',
  },
})

describeTable(offerAiPromptVersions, {
  description:
    'Version history of offer_ai_prompts: one row per saved prompt text with created_by_user_id, for audit and rollback.',
})
